import { cn } from "@/lib/utils";

interface NurslyLogoProps {
  /** Font size in px for the wordmark; the dot scales with it. */
  size?: number;
  /** Colour of the "Nurs" portion. The "ly" + dot are always Sage #52B788. */
  tone?: "forest" | "white" | "muted";
  className?: string;
}

/**
 * Single source of truth for the Nursly wordmark.
 * The accent dot is positioned in em units so it scales and stays in place
 * at any zoom level.
 */
export function NurslyLogo({ size = 22, tone = "forest", className }: NurslyLogoProps) {
  const nursClass =
    tone === "white" ? "text-white" : tone === "muted" ? "text-white/35" : "text-nursly-forest";

  return (
    <span
      aria-label="Nursly"
      className={cn(
        "relative inline-flex items-baseline font-display font-bold leading-none tracking-tight select-none",
        className,
      )}
      style={{ fontSize: `${size}px` }}
    >
      <span className={nursClass}>Nurs</span>
      <span className="text-nursly-sage">l</span>
      <span className="relative inline-block text-nursly-sage">
        y
        <span
          aria-hidden
          className="absolute rounded-full bg-nursly-sage"
          style={{
            width: "0.4em",
            height: "0.4em",
            top: "-0.3em",
            right: "-0.4em",
          }}
        />
      </span>
    </span>
  );
}

/**
 * Inline-SVG version of the Nursly wordmark for email templates.
 * Self-contained: no web fonts, no React-only CSS. Use as raw string in email HTML.
 *
 * Renders at a fixed height; width auto-scales. Sage dot positioned relative to
 * the wordmark using SVG units so it stays in place at any email-client zoom.
 */
export function nurslyLogoSvg(opts: { height?: number; tone?: "forest" | "white" } = {}) {
  const height = opts.height ?? 24;
  const nursColor = opts.tone === "white" ? "#FFFFFF" : "#1B4332";
  const sage = "#52B788";
  // Viewbox sized so text + dot fits comfortably; aspect tuned for "Nursly" + dot.
  return `<svg xmlns="http://www.w3.org/2000/svg" height="${height}" viewBox="0 -4 140 40" role="img" aria-label="Nursly">
  <text x="0" y="28" font-family="Georgia, 'Times New Roman', serif" font-weight="700" font-size="30" fill="${nursColor}">Nurs</text>
  <text x="68" y="28" font-family="Georgia, 'Times New Roman', serif" font-weight="700" font-size="30" fill="${sage}">ly</text>
  <circle cx="90" cy="2" r="6" fill="${sage}" />
</svg>`;
}
